<template>
    <main class="max-w-5xl m-5 mb-10 w-full">
        <AppLegend />
        <ValidationObserver v-slot="{ invalid, errors, dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.name')"
                        v-model="contractualDocument.name"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        :disabled="!isAdmin"
                        @blur="save()"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="contractualDocument.code"
                        class="col-span-2 md:col-span-1"
                        :disabled="!isAdmin"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="contractualDocument.emitter"
                        :options="bundles"
                        :disabled="!isAdmin"
                        @input="save()"
                    ></app-bundle-picker>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        v-model="contractualDocument.emissionDueDate"
                        class="col-span-2 md:col-span-1"
                        :label="$t('contractualDocuments.emissionDueDate')"
                        :disabled="!isAdmin"
                        @input="save()"
                    ></app-date-input>

                    <app-date-input
                        v-model="contractualDocument.emissionDate"
                        class="col-span-2 md:col-span-1"
                        :label="$t('contractualDocuments.emissionDate')"
                        :disabled="!isAdmin"
                        @input="save()"
                    />
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('contractualDocuments.attachments')">
                    <app-multiple-upload-button
                        v-if="isAdmin || isEmitter"
                        class="col-span-2 text-sm offline:hidden"
                        icon="icon-paperclip"
                        :label="$t('contractualDocuments.newAttachment')"
                        :end-point="`/api/projects/${$route.params.projectId}/contractualDocuments/${contractualDocument.id}/attachment`"
                        method="PUT"
                        :name="$t('contractualDocuments.newAttachment')"
                        accept=".pdf"
                        :disabled="contractualDocument.signatureRequests.length != 0"
                    />
                    <table class="col-span-2">
                        <template v-for="attachment in contractualDocument.attachments">
                            <tr :v-key="attachment.id" class="border-b hover:bg-gray-200">
                                <td>
                                    <app-file-link
                                        :fileName="attachment.fileName"
                                        :url="attachment.url"
                                        :to="{
                                            name: 'fileViewer',
                                            params: {
                                                projectId: $route.params.projectId,
                                            },
                                            query: {
                                                url: attachment.url,
                                                fileName: attachment.fileName,
                                                backName: $t('contractualDocuments.title'),
                                            },
                                        }"
                                    ></app-file-link>
                                </td>
                                <td class="p-2" style="width: 30px">
                                    <app-button
                                        size="mini"
                                        variant="danger"
                                        aria-label="remove document"
                                        icon="icon-trash-can-outline"
                                        @click="onDeleteAttachment(attachment)"
                                        v-if="isAdmin && contractualDocument.signatureRequests.length === 0"
                                    />
                                </td>
                            </tr>
                        </template>
                    </table>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('contractualDocuments.signers')">
                    <div class="col-span-2 flex gap-4">
                        <app-multi-picker
                            v-model="selectedBundles"
                            @close="onPickerClose"
                            :options="bundles"
                            :disabled="!isAdmin"
                            variant="button"
                            :show-label="false"
                            :show-input="false"
                            :label="$t('contractualDocuments.signers')"
                            :title="$t('contractualDocuments.signers')"
                        >
                            <template v-slot:option="{ option }">
                                <app-bundle :bundle="option" />
                            </template>
                        </app-multi-picker>
                    </div>

                    <div class="col-span-2 flex flex-col gap-4">
                        <ul class="flex-grow overflow-auto">
                            <li v-if="signers.length === 0" class="text-center italic text-gray-700 text-sm">
                                {{ $t('commons.emptyList') }}
                            </li>
                            <li
                                v-for="signer in signers"
                                :v-key="signer.id"
                                class="border p-2 flex flex-col hover:bg-gray-200"
                            >
                                <a :id="'uuid_' + signer.id"></a>
                                <div class="flex items-center">
                                    <slot name="item">
                                        <div class="w-full flex items-center">
                                            <app-select
                                                v-model="signer.groupId"
                                                :disabled="!isAdmin || signer.isInvited"
                                            >
                                                <option v-for="(_, index) in signers" :value="(index + 1).toString()">
                                                    {{ index + 1 }}
                                                </option>
                                            </app-select>
                                            <div class="p-1 items-baseline flex flex-col gap-2">
                                                <div>
                                                    <span class="text-xs">
                                                        {{
                                                            signer.bundle.reference
                                                                ? signer.bundle.reference + ' - '
                                                                : ''
                                                        }}
                                                    </span>
                                                    <span>
                                                        {{ signer.bundle.name }}
                                                    </span>
                                                    <div
                                                        class="flex items-center text-xs text-red-700"
                                                        v-if="!signer.bundle.signer"
                                                    >
                                                        <icon-alert-circle-outline></icon-alert-circle-outline>
                                                        {{ $t('contractualDocuments.noSigner') }}
                                                    </div>
                                                </div>
                                                <span v-if="signer.bundle.signer" class="text-sm">
                                                    {{ signer.bundle.signer.lastName.toUpperCase() }}
                                                    {{ signer.bundle.signer.firstName }} -
                                                    {{ signer.bundle.signer.email }}
                                                </span>
                                            </div>
                                        </div>
                                    </slot>
                                    <div class="flex items-center justify-end">
                                        <app-button
                                            size="mini"
                                            @click="onUncheck(signer.bundle.id)"
                                            v-if="isAdmin && !signer.isInvited"
                                            variant="danger"
                                            aria-label="remove item"
                                            icon="icon-trash-can-outline"
                                        />
                                        <span v-if="signer.isInvited">
                                            <span
                                                :title="$t('contractualDocuments.signerStatus.' + signer.status)"
                                                v-if="signer.status === 'signed'"
                                            >
                                                <icon-draw-pen class="text-green-500" />
                                            </span>
                                            <span
                                                :title="$t('contractualDocuments.signerStatus.' + signer.status)"
                                                v-else-if="signer.status === 'notified'"
                                            >
                                                <icon-draw-pen class="text-gray-500" />
                                            </span>
                                            <span
                                                :title="$t('contractualDocuments.signerStatus.' + signer.status)"
                                                v-else
                                            >
                                                <icon-draw-pen class="text-red-500" />
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div class="flex justify-end w-full">
                        <app-button
                            v-if="isAdmin"
                            class="text-sm"
                            variant="primary"
                            :label="$t('contractualDocuments.sendInviteToSign', { nextGroupId: nextGroupId })"
                            @click="inviteToSign"
                        />
                    </div>
                </app-fieldset>
                <app-save-on-leave
                    :dirty="dirty"
                    :saveFn="() => save(true)"
                    v-if="isAdmin || isEmitter"
                ></app-save-on-leave>
            </form>
        </ValidationObserver>
        <app-quick-actions
            :options="quickActions"
            @choose="$event.run()"
            v-if="isAdmin || isEmitter"
        ></app-quick-actions>
    </main>
</template>

<script>
import AppLegend from '../../components/appLegend/AppLegend';
import { getMapById, sanitize, sortBy } from '@/services/sanitize.service';
import AppInputText from '../../components/appInputText/AppInputText';
import AppFieldset from '../../components/appFieldset/AppFieldset';
import AppErrors from '../../components/appErrors/AppErrors';
import AppDateInput from '../../components/appDateInput/AppDateInput';
import AppPicker from '../../components/appPicker/AppPicker';
import AppButton from '../../components/appButton/AppButton';
import AppBundlePicker from '../../components/appBundlePicker';
import AppMultiPicker from '../../components/appMultiPicker/AppMultiPicker';
import { confirm } from '../dialogs/dialogs.service';

import contractualDocumentsService, {
    createContractualDocument,
    getContractualDocument,
    getSignatures,
    inviteToSign,
    removeContractualDocument,
    updateContractualDocument,
} from './contractualDocuments.service';
import AppFileLink from '../../components/appFileLink/AppFileLink';
import { updateBreadCrumbs } from '@/state/state';
import AppQuickActions from '../../components/appQuickActions/AppQuickActions';
import AppBackButton from '../../components/appBackButton/AppBackButton';
import AppSaveOnLeave from '@/components/AppSaveOnLeave';
import { getCalendar } from '@/features/planning/agenda/agenda.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getProject } from '@/features/projects/projects.service';
import AppMultipleUploadButton from '@/components/app-multipleUploadButton/AppMultipleUploadButton';
import AppDropDownButton from '@/components/appDropDownButton/AppDropDownButton.vue';
import IconArrowAll from '@/icons/IconArrowAll.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';
import AppTimeInput from '@/components/appTimeInput/AppTimeInput.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import IconAlertCircleOutline from '@/icons/IconAlertCircleOutline.vue';
import { getContacts } from '@/features/contacts/contacts.service';
import IconDrawPen from '@/icons/IconDrawPen.vue';

export default {
    components: {
        IconDrawPen,
        IconAlertCircleOutline,
        AppSelect,
        AppTimeInput,
        AppBundle,
        IconArrowAll,
        AppDropDownButton,
        AppMultipleUploadButton,
        AppSaveOnLeave,
        AppBackButton,
        AppQuickActions,
        AppFileLink,
        AppMultiPicker,
        AppBundlePicker,
        AppButton,
        AppPicker,
        AppDateInput,
        AppErrors,
        AppFieldset,
        AppInputText,
        AppLegend,
    },
    created() {
        this.init();
    },
    watch: {
        $route: function () {
            this.init();
        },
    },
    computed: {
        nextGroupId() {
            return (
                sortBy(
                    this.contractualDocument.signers.filter((signer) => !signer.signed),
                    'groupId',
                )
                    .reverse()
                    .pop()?.groupId || 1
            );
        },
        signers() {
            if (this.contractualDocument) {
                /**
                 * signatureRequests = [
                 *   {
                 *     "info": {
                 *       ...
                 *       "email": "user@example.com",
                 *     },
                 *     "status": "initiated",
                 *     ...
                 *   }
                 * ]
                 */
                const invitedSigners = this.contractualDocument.signatureRequests
                    .reduce((acc, signatureRequest) => [...acc, ...signatureRequest.signers], [])
                    .reduce((acc, signer) => ({ ...acc, [signer.info.email]: signer.status }), {});
                return this.contractualDocument.signers.map((signer) => ({
                    ...signer,
                    isInvited: signer.bundle.signer ? !!invitedSigners[signer.bundle.signer.email] : false,
                    status: signer.bundle.signer ? invitedSigners[signer.bundle.signer.email] : null,
                }));
            } else {
                return [];
            }
        },
    },
    // TODO avoid multiple contact if signer for multiple bundle
    methods: {
        filterStringFunction(bundle) {
            return bundle.reference + ' ' + bundle.name;
        },
        onPickerClose() {
            const bundleToAdd = this.selectedBundles.filter(
                (bundle) => !this.contractualDocument.signers.find((signer) => signer.bundleId === bundle.id),
            );
            const bundleToKeep = this.contractualDocument.signers.filter(
                (signer) => !!signer.isInvited || this.selectedBundles.find((bundle) => bundle.id === signer.bundleId),
            );
            const newGroupId =
                this.contractualDocument.signers.reduce(
                    (acc, signer) => (parseInt(signer.groupId) > acc ? parseInt(signer.groupId) : acc),
                    0,
                ) + 1;
            this.contractualDocument.signers = [
                ...bundleToKeep,
                ...bundleToAdd.map((bundle) => ({
                    bundleId: bundle.id,
                    bundle,
                    groupId: newGroupId.toString(),
                })),
            ];
            this.updateSigners();
        },
        onUncheck(bundleId) {
            this.contractualDocument.signers = this.contractualDocument.signers.filter(
                (signer) => bundleId !== signer.bundleId,
            );
            this.updateSigners();
        },
        updateSigners() {
            contractualDocumentsService.updateContractualDocument(this.$route.params.projectId, {
                id: this.$route.params.contractualDocumentId,
                signers: sortBy(
                    this.contractualDocument.signers.map((signer) => ({
                        bundleId: signer.bundleId,
                        groupId: signer.groupId,
                    })),
                    'groupId',
                ),
            });
        },
        init() {
            this.subscriptions = [
                getCalendar(this.$route.params.projectId).subscribe((agenda) => (this.agenda = agenda)),
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getContractualDocument(this.$route.params.projectId, this.$route.params.contractualDocumentId),
                    getProject(this.$route.params.projectId),
                    getContacts(this.$route.params.projectId),
                ]).subscribe(async ([bundles, contractualDocument, project, contacts]) => {
                    const contactMap = getMapById(contacts);
                    updateBreadCrumbs({ contractualDocumentName: contractualDocument.name });
                    this.bundles = sortBy(
                        bundles.map((bundle) => {
                            return {
                                ...bundle,
                                signer: contactMap[bundle.signerId],
                            };
                        }),
                        (bundle) => `${bundle.reference}${bundle.name}`,
                    );
                    const bundleMap = getMapById(this.bundles);
                    const bundle = bundleMap[contractualDocument.bundleId];

                    const signatureRequests = await getSignatures(
                        this.$route.params.projectId,
                        this.$route.params.contractualDocumentId,
                    );
                    this.contractualDocument = {
                        ...contractualDocument,
                        bundle,
                        emitter: bundleMap[contractualDocument.emitterId],
                        signers: sortBy(
                            contractualDocument.signers.map((signer) => ({
                                ...signer,
                                bundle: bundleMap[signer.bundleId],
                            })),
                            'groupId',
                        ),
                        signatureRequests: signatureRequests || [],
                    };
                    this.selectedBundles = contractualDocument.signers.map((signer) => bundleMap[signer.bundleId]);
                    this.isAdmin = project.me.allowedFeatures.includes('project_contractualDocuments');
                    this.isEmitter = project.me.bundleIds.includes(contractualDocument.emitterId);
                }),
            ];
        },
        async inviteToSign() {
            if (await confirm(this.$t('contractualDocuments.confirmInvite'))) {
                return inviteToSign(this.$route.params.projectId, this.$route.params.contractualDocumentId);
            }
        },
        async onDeleteAttachment({ url }) {
            if (await confirm(this.$t('contractualDocuments.confirmMessage'))) {
                return updateContractualDocument(this.$route.params.projectId, {
                    id: this.$route.params.contractualDocumentId,
                    attachments: this.contractualDocument.attachments
                        .filter((attachment) => attachment.url !== url)
                        .map((attachment) => ({ url: attachment.url, fileName: attachment.fileName })),
                });
            }
        },
        async save() {
            const contractualDocumentEntity = {
                ...sanitize(this.contractualDocument, ['emitter']),
                emitterId: this.contractualDocument.emitter ? this.contractualDocument.emitter.id : null,
            };
            await updateContractualDocument(this.$route.params.projectId, {
                id: this.contractualDocument.id,
                ...contractualDocumentEntity,
            });
            if (this.$refs.observer) {
                this.$refs.observer.reset();
            }
        },
        async removeContractualDocument() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                await removeContractualDocument(this.$route.params.projectId, this.contractualDocument.id);
                this.$router.push({
                    name: 'contractualDocuments',
                    params: {
                        projectId: this.$route.params.projectId,
                    },
                });
            }
        },
    },
    data() {
        return {
            isAdmin: false,
            isEmitter: false,
            bundles: [],
            selectedBundles: [],
            subscriptions: [],
            contractualDocument: {
                name: '',
                emitter: null,
                code: '',
                emissionDueDate: null,
                emissionDate: null,
                attachments: [],
                signers: [],
                signatureRequests: [],
            },
            quickActions: [
                {
                    name: this.$t('contractualDocuments.newContractualDocument'),
                    run: async () => {
                        const result = await createContractualDocument(this.$route.params.projectId, {});
                        await this.$router.push({
                            name: 'contractualDocument',
                            params: {
                                projectId: this.$route.params.projectId,
                                contractualDocumentId: result.id,
                            },
                        });
                    },
                },
                {
                    name: this.$t('contractualDocuments.removeContractualDocument'),
                    run: () => {
                        this.removeContractualDocument(this.$route.params.projectId);
                    },
                },
            ],
        };
    },
};
</script>
